import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { SEO } from "@/lib/seo";
import { COMPANY, CITIES, SERVICES, PROCESS_STEPS } from "@/lib/constants";
import { api } from "@/lib/api";
import Reveal, { SectionHead } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import BeforeAfter from "@/components/BeforeAfter";
import PavingCanvas from "@/components/PavingCanvas";
import { WhatsAppIcon } from "@/components/WhatsAppIcon";

const HERO_LINES = ["Perfekcyjne", "nawierzchnie", "z kostki", "brukowej."];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const photoY = useTransform(scrollYProgress, [0, 1], [0, 90]);

  return (
    <section ref={ref} className="grain relative flex min-h-screen items-center overflow-hidden bg-ink" data-testid="hero">
      <div className="absolute inset-0 opacity-40">
        <PavingCanvas />
      </div>
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_30%_40%,transparent_0%,#0B0C0E_75%)]" />

      <div className="relative z-10 mx-auto grid w-full max-w-7xl gap-12 px-4 pt-28 pb-16 sm:px-8 lg:grid-cols-12 lg:items-center">
        <div className="lg:col-span-7">
          <p className="eyebrow mb-6">Brukarstwo · Łęczna · Lublin · Świdnik</p>
          <h1 className="font-display text-[17vw] font-black uppercase leading-[0.95] tracking-tight text-pearl sm:text-7xl lg:text-8xl" data-testid="hero-title">
            {HERO_LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${line === "brukowej." ? "text-amber" : ""}`}
                  initial={{ y: "115%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.15 + i * 0.13, ease: [0.22, 1, 0.36, 1] }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p
            className="mt-7 max-w-xl text-base leading-relaxed text-stonemute sm:text-lg"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.75 }}
            data-testid="hero-sub"
          >
            Układamy kostkę brukową, płyty tarasowe i płyty ceramiczne. Podjazdy, parkingi, opaski, tarasy i brukarstwo artystyczne — od A do Z, z pomocą w projekcie i doborze materiałów.
          </motion.p>
          <motion.div className="mt-9 flex flex-wrap items-center gap-4" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.9 }}>
            <Link to="/kontakt" data-testid="hero-cta-wycena" className="btn-amber px-7 py-3.5 font-mono text-xs font-medium uppercase tracking-[0.2em]">
              Darmowa wycena
            </Link>
            <a href={`tel:${COMPANY.phoneRaw}`} data-testid="hero-cta-call" className="btn-ghost px-7 py-3.5 font-mono text-xs uppercase tracking-[0.2em]">
              {COMPANY.phone}
            </a>
          </motion.div>
          <motion.div className="mt-12 flex flex-wrap gap-6 font-mono text-[10px] uppercase tracking-[0.2em] text-stonemute" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 1.1 }}>
            <span>Od A do Z</span>
            <span className="text-amber">◆</span>
            <span>Projekt i doradztwo</span>
            <span className="text-amber">◆</span>
            <span>Pomoc w zakupie materiałów</span>
          </motion.div>
        </div>

        <motion.div className="relative hidden lg:col-span-5 lg:block" style={{ y: photoY }}>
          <div className="notch relative overflow-hidden">
            <img src="/photos/hero-dom.webp" alt="Podjazd z płyt wielkoformatowych przy domu jednorodzinnym — realizacja AD Brikman, Łęczna" className="h-[560px] w-full object-cover" data-testid="hero-photo" />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-transparent" />
          </div>
          <div className="glass notch-sm absolute -bottom-5 -left-8 px-6 py-4">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber">Obszar działania</p>
            <p className="font-display mt-1 text-xl font-bold uppercase text-pearl">{CITIES.slice(0, 3).join(" · ")} i okolice</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

const ServicesBento = () => (
  <section className="mx-auto max-w-7xl px-4 py-24 sm:px-8 lg:py-32" data-testid="home-services">
    <SectionHead eyebrow="Co robimy" title="Usługi brukarskie" sub="Od podjazdu po brukarstwo artystyczne — wykonujemy prace od A do Z lub w zakresie preferowanym przez klienta." />
    <div className="grid grid-cols-12 gap-4">
      <Reveal className="col-span-12 md:col-span-8" delay={0}>
        <Link to="/uslugi" data-testid="bento-kostka" className="card-edge group relative block h-[340px] overflow-hidden md:h-[420px]">
          <img src={SERVICES[0].photo} alt={SERVICES[0].title} loading="lazy" className="absolute inset-0 h-full w-full object-cover opacity-60 transition-transform duration-700 group-hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-transparent" />
          <div className="absolute bottom-0 p-8">
            <p className="eyebrow mb-3">01 · Podjazdy i parkingi</p>
            <h3 className="font-display text-4xl font-extrabold uppercase text-pearl">{SERVICES[0].title}</h3>
            <p className="mt-3 max-w-md text-sm text-stonemute">{SERVICES[0].desc}</p>
          </div>
        </Link>
      </Reveal>

      <Reveal className="col-span-12 md:col-span-4" delay={0.1}>
        <div className="card-edge notch-sm flex h-full flex-col justify-between p-8" data-testid="bento-contact">
          <div>
            <p className="eyebrow mb-5">Wycena bezpłatna</p>
            <a href={`tel:${COMPANY.phoneRaw}`} data-testid="bento-phone" className="font-display text-4xl font-extrabold tracking-wide text-pearl transition-colors duration-300 hover:text-amber sm:text-5xl">
              {COMPANY.phone}
            </a>
            <a href={`mailto:${COMPANY.email}`} data-testid="bento-email" className="mt-4 block break-all text-sm text-stonemute transition-colors duration-300 hover:text-amber">
              {COMPANY.email}
            </a>
            <p className="mt-3 text-sm leading-relaxed text-stonemute">
              {COMPANY.street}
              <br />
              {COMPANY.postal}
            </p>
          </div>
          <Link to="/kontakt" data-testid="bento-contact-cta" className="btn-amber mt-8 inline-block px-6 py-3 text-center font-mono text-xs font-medium uppercase tracking-[0.2em]">
            Umów wizję lokalną
          </Link>
        </div>
      </Reveal>

      <Reveal className="col-span-12 md:col-span-4" delay={0.15}>
        <div className="card-edge h-full p-8" data-testid="bento-list">
          <p className="eyebrow mb-5">Pozostałe usługi</p>
          <ul className="space-y-3">
            {[SERVICES[2], SERVICES[3], SERVICES[4], SERVICES[5], SERVICES[7]].map((s) => (
              <li key={s.id} className="flex items-center gap-3 border-b border-white/5 pb-3">
                <span className="h-1.5 w-1.5 rotate-45 bg-amber" />
                <Link to="/uslugi" className="font-display text-lg font-bold uppercase text-pearl transition-colors duration-300 hover:text-amber">
                  {s.title}
                </Link>
              </li>
            ))}
          </ul>
          <Link to="/uslugi" data-testid="bento-all-services" className="mt-5 inline-block font-mono text-[11px] uppercase tracking-[0.2em] text-amber hover:text-amberdeep">
            Zobacz wszystkie →
          </Link>
        </div>
      </Reveal>

      <Reveal className="col-span-12 md:col-span-8" delay={0.2}>
        <Link to="/uslugi" data-testid="bento-artystyczne" className="card-edge group relative block h-[300px] overflow-hidden md:h-[340px]">
          <img src={SERVICES[6].photo} alt={SERVICES[6].title} loading="lazy" className="absolute inset-0 h-full w-full object-cover opacity-50 transition-transform duration-700 group-hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/50 to-transparent" />
          <div className="absolute bottom-0 p-8">
            <p className="eyebrow mb-3">Nasza specjalność</p>
            <h3 className="font-display text-3xl font-extrabold uppercase text-pearl sm:text-4xl">{SERVICES[6].title}</h3>
            <p className="mt-3 max-w-lg text-sm text-stonemute">{SERVICES[6].desc}</p>
          </div>
        </Link>
      </Reveal>
    </div>
  </section>
);

const TransformSection = () => (
  <section className="border-y border-white/10 bg-graph grain" data-testid="home-transform">
    <div className="paver-bg">
      <div className="mx-auto grid max-w-7xl gap-14 px-4 py-24 sm:px-8 lg:grid-cols-2 lg:items-center lg:py-32">
        <div>
          <SectionHead
            eyebrow="Przed i po"
            title="Z ziemi — w nawierzchnię na dekady"
            sub="Każdą inwestycję prowadzimy etapami: od niwelacji i podbudowy, po ostatnią fugę. Przesuń suwak, aby zobaczyć różnicę, jaką robi profesjonalne wykonanie."
          />
          <div className="space-y-6">
            {PROCESS_STEPS.slice(0, 3).map((s, i) => (
              <Reveal key={s.n} delay={i * 0.08}>
                <div className="flex gap-5" data-testid={`process-step-${s.n}`}>
                  <span className="font-mono text-sm text-amber">{s.n}</span>
                  <div>
                    <p className="font-display text-lg font-bold uppercase text-pearl">{s.title}</p>
                    <p className="mt-1 text-sm text-stonemute">{s.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.3}>
            <Link to="/uslugi" data-testid="transform-process-link" className="mt-8 inline-block font-mono text-[11px] uppercase tracking-[0.2em] text-amber hover:text-amberdeep">
              Zobacz cały proces krok po kroku →
            </Link>
          </Reveal>
        </div>
        <Reveal delay={0.1}>
          <BeforeAfter
            before="https://images.unsplash.com/photo-1652303713917-2666b8bee507?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
            after="https://images.pexels.com/photos/8134846/pexels-photo-8134846.jpeg?auto=compress&cs=tinysrgb&w=1200"
            alt="Podjazd przed i po ułożeniu kostki brukowej"
          />
        </Reveal>
      </div>
    </div>
  </section>
);

const RegionSection = () => (
  <section className="mx-auto max-w-7xl px-4 py-24 sm:px-8 lg:py-32" data-testid="home-region">
    <SectionHead eyebrow="Gdzie działamy" title="Lubelszczyzna — nasz region" sub="Jesteśmy z Łęcznej. Znamy lokalne grunty, warunki wodne i sprawdzonych producentów kostki. Działamy na obiektach prywatnych oraz dla firm i administratorów nieruchomości." />
    <div className="flex flex-wrap gap-3">
      {CITIES.map((c, i) => (
        <Reveal key={c} delay={i * 0.03}>
          <span className="card-edge notch-sm inline-block px-5 py-2.5 font-display text-lg font-bold uppercase text-pearl" data-testid={`region-city-${c.toLowerCase()}`}>
            {c}
          </span>
        </Reveal>
      ))}
    </div>
  </section>
);

const WhySection = () => (
  <section className="border-y border-white/10 bg-graph grain" data-testid="home-why">
    <div className="mx-auto max-w-7xl px-4 py-24 sm:px-8 lg:py-32">
      <SectionHead eyebrow="Dlaczego my" title="Perfekcjonizm. Profesjonalizm. Rzetelność." />
      <div className="grid gap-4 md:grid-cols-3">
        {[
          { t: "Perfekcjonizm", d: "Równe linie, spójna fuga, przemyślane spadki. Każdą kostkę układamy, jakby była nasza.", icon: "M12 2l3 7h7l-5.5 4.5L18 21l-6-4-6 4 1.5-7.5L2 9h7z" },
          { t: "Profesjonalizm", d: "Prawidłowa podbudowa, odwodnienie i terminowość. Doradzamy w projekcie i doborze materiałów.", icon: "M2 20h20M4 20V10l8-6 8 6v10M9 20v-6h6v6" },
          { t: "Rzetelność", d: "Jasne ustalenia, uczciwa wycena i prace wykonane w całości — od A do Z lub w wybranym zakresie.", icon: "M20 6L9 17l-5-5" },
        ].map((p, i) => (
          <Reveal key={p.t} delay={i * 0.1}>
            <div className="card-edge h-full p-8" data-testid={`why-${p.t.toLowerCase()}`}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#FFB800" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d={p.icon} /></svg>
              <h3 className="font-display mt-5 text-2xl font-extrabold uppercase text-pearl">{p.t}</h3>
              <p className="mt-3 text-sm leading-relaxed text-stonemute">{p.d}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const BlogTeaser = () => {
  const [posts, setPosts] = useState([]);
  useEffect(() => {
    api.get("/posts", { params: { limit: 3 } }).then((r) => setPosts(r.data)).catch(() => {});
  }, []);

  return (
    <section className="mx-auto max-w-7xl px-4 py-24 sm:px-8 lg:py-32" data-testid="home-blog">
      <SectionHead eyebrow="Poradnik brukarski" title="Wiedza z budowy" />
      <div className="grid gap-4 md:grid-cols-3">
        {posts.map((p, i) => (
          <Reveal key={p.slug} delay={i * 0.1}>
            <Link to={`/blog/${p.slug}`} data-testid={`home-blog-${p.slug}`} className="card-edge group block h-full overflow-hidden">
              {p.cover && <img src={p.cover} alt={p.title} loading="lazy" className="h-44 w-full object-cover opacity-80 transition-transform duration-700 group-hover:scale-105" />}
              <div className="p-6">
                <p className="eyebrow mb-3">{p.category}</p>
                <h3 className="font-display text-xl font-bold uppercase leading-snug text-pearl transition-colors duration-300 group-hover:text-amber">{p.title}</h3>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.2}>
        <Link to="/blog" data-testid="home-blog-all" className="mt-10 inline-block font-mono text-[11px] uppercase tracking-[0.2em] text-amber hover:text-amberdeep">
          Cały poradnik →
        </Link>
      </Reveal>
    </section>
  );
};

const CtaBand = () => (
  <section className="grain border-t border-white/10 bg-graph" data-testid="home-cta">
    <div className="paver-bg">
      <div className="mx-auto max-w-7xl px-4 py-24 text-center sm:px-8 lg:py-32">
        <Reveal>
          <h2 className="font-display mx-auto max-w-4xl text-4xl font-black uppercase leading-tight text-pearl sm:text-6xl">
            Planujesz podjazd, taras <span className="text-amber">lub opaskę?</span>
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mx-auto mt-6 max-w-xl text-stonemute">
            Zadzwoń lub napisz — doradzimy, pomożemy dobrać materiały i wycenimy bezpłatnie.
          </p>
        </Reveal>
        <Reveal delay={0.2}>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <a href={`tel:${COMPANY.phoneRaw}`} data-testid="cta-band-call" className="btn-amber px-8 py-4 font-mono text-sm font-medium tracking-widest">
              {COMPANY.phone}
            </a>
            <a href={`mailto:${COMPANY.email}`} data-testid="cta-band-mail" className="btn-ghost px-8 py-4 font-mono text-sm tracking-widest">
              {COMPANY.email}
            </a>
            <a href={COMPANY.whatsapp} target="_blank" rel="noopener noreferrer" data-testid="cta-band-whatsapp" className="btn-ghost flex items-center gap-2 px-8 py-4 font-mono text-sm tracking-widest">
              <WhatsAppIcon className="text-[#25D366]" />
              WhatsApp
            </a>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

const Home = () => (
  <>
    <SEO
      title="Kostka brukowa Łęczna, Lublin, Świdnik | AD Brikman Usługi Brukarskie"
      description="Układanie kostki brukowej, płyty tarasowe, płyty ceramiczne, palisady i brukarstwo artystyczne. Łęczna, Lublin, Świdnik, Łuszczów, Cyców i okolice. Podjazdy, tarasy, opaski, parkingi. Darmowa wycena: 535 616 324."
      path="/"
    />
    <Hero />
    <Marquee />
    <ServicesBento />
    <TransformSection />
    <RegionSection />
    <WhySection />
    <BlogTeaser />
    <CtaBand />
  </>
);

export default Home;
